import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Construction } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { Button } from '@/components/ui/button';
import { findReport, isReportAvailable } from '@/features/reports/catalogue';
import { ReportPage } from '@/features/reports/report-page';
import { parseSettings, type Settings } from '@/features/reports/settings';

export const Route = createFileRoute('/reports/$reportKey')({
  component: ReportRoute,
  validateSearch: (s: Record<string, unknown>): Settings => parseSettings(s),
});

/** Screen R2 (document 06): any report in the catalogue, through the shared report page. */
function ReportRoute() {
  const { reportKey } = Route.useParams();
  const settings = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const spec = findReport(reportKey);
  if (!spec || !isReportAvailable(reportKey)) {
    return (
      <>
        <PageHeader title={spec?.name ?? 'Report not found'} />
        <div className="flex flex-col items-center justify-center rounded-lg border bg-card px-6 py-16 text-center">
          {spec ? (
            <>
              <Construction className="mb-3 size-8 text-primary" aria-hidden />
              <p className="text-base font-medium" data-testid="not-available">
                This feature isn&apos;t available yet.
              </p>
            </>
          ) : (
            <p className="text-base font-medium">There is no report called {reportKey}.</p>
          )}
          <Button asChild className="mt-6">
            <Link to="/reports">Back to report list</Link>
          </Button>
        </div>
      </>
    );
  }
  return (
    <ReportPage
      key={reportKey}
      spec={spec}
      settings={settings}
      onRun={(next) => void navigate({ search: next })}
    />
  );
}
